import axios from "axios";

export function downloadBlobObject(blob: Blob, fileName: string) {
  const anchor = document.createElement("a");
  const blobURL = URL.createObjectURL(blob);
  anchor.href = blobURL;
  anchor.download = fileName;
  anchor.click();
}

export async function convertBase64ToBlob(base64: string, type: string) {
  return fetch(`data:${type};base64,${base64}`).then((res) => res.blob());
}

export function downloadS3File(
  file: string,
  fileName = "",
  includeCacheBuster = true
) {
  const url = includeCacheBuster
    ? `${file}${file.includes("?") ? "&" : "?"}_cb=${Date.now()}`
    : file;

  // Helper to extract filename from URL when fileName isn't provided
  const filenameFromUrl = (u: string) => {
    try {
      const parsed = new URL(u, window.location.href);
      const pathname = parsed.pathname;
      const name = pathname.substring(pathname.lastIndexOf("/") + 1);
      return name || null;
    } catch (e) {
      return null;
    }
  };

  // Try to fetch the resource as a blob so we can force the download filename.
  // If fetch fails (CORS or network), fall back to a simple anchor click.
  (async () => {
    try {
      const response = await axios.get(url, { responseType: "blob" });
      if (response && response.status === 200) {
        const blobURL = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = blobURL;
        anchor.download = fileName || filenameFromUrl(file) || "download";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(blobURL);
        return;
      }
    } catch (err) {
      // ignore and fall through to fallback
    }

    // Fallback: plain anchor click (may be subject to cross-origin browser behavior)
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName || filenameFromUrl(file) || "download";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  })();
}

export async function downloadFileFromURL(url: string, fileName = "file") {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function fetchAndConvertToDataURL(
  url: string
): Promise<string | null> {
  try {
    const response = await axios.get(url, {
      responseType: "blob",
      headers: { Referer: window.location.host },
    });
    if (response.status !== 200) {
      throw new Error("Failed to fetch resource");
    }
    const reader = new FileReader();
    const dataUrlPromise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert image to data URL"));
        }
      };
      reader.onerror = () => {
        reject(new Error("Failed to read image data"));
      };
    });
    reader.readAsDataURL(response.data);
    return dataUrlPromise;
  } catch (error) {
    throw new Error("Error");
  }
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}
